import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import GlobalStyles from '@mui/material/GlobalStyles'
import type { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import type { NavItemConfig } from '@/types/nav'
import { ChatProvider, useChat } from '@/contexts/chat-context'
import { TypeformNavProvider } from '@/contexts/typeform-nav-context'
import { useFilteredNavItems } from '@/hooks/use-filtered-nav-items'
import { useHealthAccess } from '@/hooks/use-health-access'
import { useSidebarCollapsed } from '@/hooks/use-sidebar-collapsed'
import { useUser } from '@/hooks/use-user'
import { AuthenticationGuard } from '@/components/auth/authentication-guard'
import { AuthorizationGuard } from '@/components/auth/authorization-guard'
import { PlatformLiveConnection } from '@/components/core/platform-live-connection'
import { ChatPanel } from '@/components/dashboard/assistant/chat-panel'
import { getLayoutConfig } from '@/components/dashboard/layout/config'
import { WorkspacesProvider } from '@/components/dashboard/layout/workspaces/workspaces-context'

import { ActiveAlertsBanner } from './active-alerts-banner'
import { MainNav } from './nav/main-nav'
import { SideNav } from './nav/side-nav'

interface LayoutProps {
	children: ReactNode
}

interface DashboardLayoutContentProps {
	children: ReactNode
	filteredItems: NavItemConfig[]
	mini: boolean
	toggleMini: () => void
}

function DashboardLayoutContent({ children, filteredItems, mini, toggleMini }: DashboardLayoutContentProps) {
	const { isOpen: isChatOpen } = useChat()
	// On mobile the assistant renders as a page in document flow instead of a
	// fixed overlay — hide the dashboard behind it.
	const hideForChat = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')) && isChatOpen
	return (
		<TypeformNavProvider>
			<GlobalStyles
				styles={{
					// Keep the scrollbar width constant while chat is open —
					// otherwise it flips the sm breakpoint each frame and the
					// panel flickers between floating and full screen.
					// (overflow-y: scroll — scrollbar-gutter alone is a no-op
					// on the viewport in Chrome.)
					...(isChatOpen ? { html: { overflowY: 'scroll' } } : {}),
					body: {
						'--Layout-gap': '0px',
						'--MainNav-height': '56px',
						'--MainNav-zIndex': 1000,
						'--SideNav-width': mini ? '70px' : '280px',
						'--SideNav-header-height': '40px',
						'--SideNav-header-offset': '2px',
						'--SideNav-header-padding-top':
							'calc(var(--Layout-gap) + (var(--MainNav-height) - var(--SideNav-header-height)) / 2 + var(--SideNav-header-offset))',
						'--SideNav-toggle-top':
							'calc(var(--Layout-gap) + (var(--MainNav-height) - 36px) / 2 + var(--SideNav-header-offset))',
						'--SideNav-zIndex': 1100,
						'--MobileNav-width': '320px',
						'--MobileNav-zIndex': 1100,
					},
				}}
			/>
			<Box
				sx={{
					bgcolor: 'var(--mui-palette-background-default)',
					display: hideForChat ? 'none' : 'flex',
					flexDirection: 'column',
					position: 'relative',
					minHeight: '100%',
				}}
			>
				<SideNav items={filteredItems} mini={mini} toggleMini={toggleMini} />
				<Box
					sx={{
						'--Content-margin': '0 auto',
						'--Content-maxWidth': 'var(--maxWidth-xl)',
						'--Content-paddingX': { xs: '16px', sm: '24px' },
						'--Content-paddingY': { xs: '16px', sm: '24px', lg: '28px' },
						'--Content-paddingBottom': '100px',
						'--Content-padding': 'var(--Content-paddingY) var(--Content-paddingX) var(--Content-paddingBottom)',
						'--Content-width': '100%',
						display: 'flex',
						flex: '1 1 auto',
						flexDirection: 'column',
						pl: { lg: 'var(--SideNav-width)' },
						pt: 'calc(var(--ImpersonationBanner-height, 0px) + var(--BetterStackBanner-height, 0px))',
					}}
				>
					<MainNav items={filteredItems} sidebarMini={mini} />
					<ActiveAlertsBanner />
					<Box
						component="main"
						sx={{
							display: 'flex',
							flex: '1 1 auto',
							flexDirection: 'column',
						}}
					>
						{children}
					</Box>
				</Box>
			</Box>
			<ChatPanel />
		</TypeformNavProvider>
	)
}

export function Layout({ children }: LayoutProps) {
	const { user } = useUser()
	const [mini, toggleMini] = useSidebarCollapsed()
	const items = getLayoutConfig(user)?.navItems
	const filteredItems = useFilteredNavItems(items)
	const hasHealthAccess = useHealthAccess()

	return (
		<AuthenticationGuard>
			<AuthorizationGuard>
				<WorkspacesProvider>
					{hasHealthAccess ? <PlatformLiveConnection /> : null}
					<ChatProvider>
						<DashboardLayoutContent filteredItems={filteredItems} mini={mini} toggleMini={toggleMini}>
							{children}
						</DashboardLayoutContent>
					</ChatProvider>
				</WorkspacesProvider>
			</AuthorizationGuard>
		</AuthenticationGuard>
	)
}
